/**
 * Safe custom-rule evaluation without eval() (CSP-compatible).
 * Supports numeric/string comparisons, BETWEEN, AND/OR, and arithmetic.
 */

const normalizeRuleText = (rule) => {
  let text;
  if (typeof rule === 'string') text = rule.trim();
  else if (rule && typeof rule === 'object') {
    text = String(rule.rule || rule.text || rule.expression || '').trim();
  } else {
    text = String(rule ?? '').trim();
  }
  // Migrate legacy operators to SQL-style syntax
  return text.replace(/\s==\s/g, ' = ').replace(/\s!=\s/g, ' <> ');
};

const formatFieldValue = (value) => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'null';
  if (typeof value === 'boolean') return String(value);
  return JSON.stringify(String(value));
};

const preprocessRule = (ruleText, stock) => {
  let expr = ruleText;
  const stockFields = Object.keys(stock).sort((a, b) => b.length - a.length);

  for (const field of stockFields) {
    const regex = new RegExp(`\\b${field}\\b`, 'g');
    expr = expr.replace(regex, formatFieldValue(stock[field]));
  }

  const betweenRegex = /(?:\(\s*)?([^\s(]+)\s+BETWEEN\s+(\S+)\s+AND\s+([^\s)]+)(?:\))?/gi;
  expr = expr.replace(betweenRegex, '($1 >= $2 && $1 <= $3)');

  expr = expr.replace(/\bAND\b/gi, '&&');
  expr = expr.replace(/\bOR\b/gi, '||');

  if (/==/.test(expr)) {
    throw new Error('Use single = for equality (not ==)');
  }
  if (/!=/.test(expr)) {
    throw new Error('Use <> for not equal (not !=)');
  }

  // SQL-style <> → internal !== for the parser
  expr = expr.replace(/<>/g, '!==');

  // Single = as equality → internal === for the parser
  expr = expr.replace(/(?<![=!<>])=(?!=)/g, '===');

  expr = expr.replace(
    /(===|!==)\s*([a-zA-Z_][a-zA-Z0-9_]*)\b/g,
    (match, op, ident) => {
      if (['true', 'false', 'null', 'undefined', 'NaN'].includes(ident)) {
        return `${op} ${ident}`;
      }
      return `${op} ${JSON.stringify(ident)}`;
    }
  );

  expr = expr.replace(/\bnull\b/g, 'NaN');
  return expr;
};

const tokenize = (input) => {
  const tokens = [];
  let i = 0;

  while (i < input.length) {
    const ch = input[i];
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (ch === '(') {
      tokens.push({ type: 'lparen' });
      i += 1;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen' });
      i += 1;
      continue;
    }

    if (ch === '"' || ch === "'") {
      const quote = ch;
      i += 1;
      let str = '';
      while (i < input.length && input[i] !== quote) {
        if (input[i] === '\\') {
          i += 1;
          if (i < input.length) str += input[i];
          i += 1;
          continue;
        }
        str += input[i];
        i += 1;
      }
      if (i >= input.length) throw new Error('Unterminated string literal');
      i += 1;
      tokens.push({ type: 'string', value: str });
      continue;
    }

    if (input.startsWith('===', i)) {
      tokens.push({ type: 'op', value: '===' });
      i += 3;
      continue;
    }
    if (input.startsWith('!==', i)) {
      tokens.push({ type: 'op', value: '!==' });
      i += 3;
      continue;
    }
    if (input.startsWith('&&', i)) {
      tokens.push({ type: 'op', value: '&&' });
      i += 2;
      continue;
    }
    if (input.startsWith('||', i)) {
      tokens.push({ type: 'op', value: '||' });
      i += 2;
      continue;
    }
    if (input.startsWith('>=', i)) {
      tokens.push({ type: 'op', value: '>=' });
      i += 2;
      continue;
    }
    if (input.startsWith('<=', i)) {
      tokens.push({ type: 'op', value: '<=' });
      i += 2;
      continue;
    }

    if ('+-*/<>'.includes(ch)) {
      tokens.push({ type: 'op', value: ch });
      i += 1;
      continue;
    }

    if (/[0-9.]/.test(ch) || (ch === '-' && /[0-9]/.test(input[i + 1]))) {
      let num = '';
      if (ch === '-') {
        num += ch;
        i += 1;
      }
      while (i < input.length && /[0-9.]/.test(input[i])) {
        num += input[i];
        i += 1;
      }
      const value = parseFloat(num);
      if (!Number.isFinite(value)) throw new Error(`Invalid number: ${num}`);
      tokens.push({ type: 'number', value });
      continue;
    }

    if (/[a-zA-Z_]/.test(ch)) {
      let ident = '';
      while (i < input.length && /[a-zA-Z0-9_]/.test(input[i])) {
        ident += input[i];
        i += 1;
      }
      tokens.push({ type: 'ident', value: ident });
      continue;
    }

    throw new Error(`Unexpected character: ${ch}`);
  }

  return tokens;
};

class ExpressionParser {
  constructor(tokens) {
    this.tokens = tokens;
    this.pos = 0;
  }

  peek() {
    return this.tokens[this.pos];
  }

  consume(type, value) {
    const token = this.peek();
    if (!token || token.type !== type || (value !== undefined && token.value !== value)) {
      throw new Error('Unexpected token in rule expression');
    }
    this.pos += 1;
    return token;
  }

  parse() {
    const result = this.parseOr();
    if (this.pos < this.tokens.length) {
      throw new Error('Unexpected trailing tokens in rule expression');
    }
    return result;
  }

  parseOr() {
    let left = this.parseAnd();
    while (this.peek()?.type === 'op' && this.peek().value === '||') {
      this.consume('op', '||');
      left = Boolean(left) || Boolean(this.parseAnd());
    }
    return left;
  }

  parseAnd() {
    let left = this.parseEquality();
    while (this.peek()?.type === 'op' && this.peek().value === '&&') {
      this.consume('op', '&&');
      left = Boolean(left) && Boolean(this.parseEquality());
    }
    return left;
  }

  parseEquality() {
    let left = this.parseComparison();
    while (this.peek()?.type === 'op' && (this.peek().value === '===' || this.peek().value === '!==')) {
      const op = this.consume('op').value;
      const right = this.parseComparison();
      if (op === '===') {
        left = typeof left === 'string' && typeof right === 'string'
          ? left.toLowerCase() === right.toLowerCase()
          : left === right;
      } else {
        left = typeof left === 'string' && typeof right === 'string'
          ? left.toLowerCase() !== right.toLowerCase()
          : left !== right;
      }
    }
    return left;
  }

  parseComparison() {
    let left = this.parseAdditive();
    const compOps = ['<', '<=', '>', '>='];
    while (this.peek()?.type === 'op' && compOps.includes(this.peek().value)) {
      const op = this.consume('op').value;
      const right = this.parseAdditive();
      switch (op) {
        case '<': left = left < right; break;
        case '<=': left = left <= right; break;
        case '>': left = left > right; break;
        case '>=': left = left >= right; break;
        default: break;
      }
    }
    return left;
  }

  parseAdditive() {
    let left = this.parseMultiplicative();
    while (this.peek()?.type === 'op' && (this.peek().value === '+' || this.peek().value === '-')) {
      const op = this.consume('op').value;
      const right = this.parseMultiplicative();
      left = op === '+' ? left + right : left - right;
    }
    return left;
  }

  parseMultiplicative() {
    let left = this.parseUnary();
    while (this.peek()?.type === 'op' && (this.peek().value === '*' || this.peek().value === '/')) {
      const op = this.consume('op').value;
      const right = this.parseUnary();
      left = op === '*' ? left * right : left / right;
    }
    return left;
  }

  parseUnary() {
    if (this.peek()?.type === 'op' && this.peek().value === '-') {
      this.consume('op', '-');
      return -this.parseUnary();
    }
    return this.parsePrimary();
  }

  parsePrimary() {
    const token = this.peek();
    if (token?.type === 'number') {
      this.consume('number');
      return token.value;
    }
    if (token?.type === 'string') {
      this.consume('string');
      return token.value;
    }
    if (token?.type === 'ident') {
      this.consume('ident');
      if (token.value === 'NaN') return NaN;
      if (token.value === 'true') return true;
      if (token.value === 'false') return false;
      if (token.value === 'null') return null;
      throw new Error(`Unknown identifier: ${token.value}`);
    }
    if (token?.type === 'lparen') {
      this.consume('lparen');
      const value = this.parseOr();
      this.consume('rparen');
      return value;
    }
    throw new Error('Expected value in rule expression');
  }
};

export const evaluateExpression = (expression) => {
  const tokens = tokenize(expression.trim());
  if (tokens.length === 0) throw new Error('Empty rule expression');
  return new ExpressionParser(tokens).parse();
};

export const evaluateCustomRule = (rule, stock) => {
  const ruleText = normalizeRuleText(rule);
  if (!ruleText) return false;

  try {
    const expression = preprocessRule(ruleText, stock);
    return Boolean(evaluateExpression(expression));
  } catch (error) {
    console.error('Error evaluating rule:', error, ruleText);
    return false;
  }
};

export const applyCustomRules = (stockData, rules) => {
  if (!Array.isArray(stockData)) return [];

  const buyRules = Array.isArray(rules?.buyRules) ? rules.buyRules : [];
  const sellRules = Array.isArray(rules?.sellRules) ? rules.sellRules : [];
  if (!buyRules.length && !sellRules.length) {
    return stockData;
  }

  const processedData = JSON.parse(JSON.stringify(stockData));

  processedData.forEach((stock) => {
    stock.new_status = 'hold';

    if (buyRules.length > 0 && buyRules.some((rule) => evaluateCustomRule(rule, stock))) {
      stock.new_status = 'buy';
    }

    if (sellRules.length > 0 && sellRules.some((rule) => evaluateCustomRule(rule, stock))) {
      stock.new_status = 'sell';
    }
  });

  return processedData;
};
